import React, { createContext, useState, useContext, ReactNode, useEffect } from 'react';
import { translations, Language } from '../translations';

interface LanguageContextType {
    language: Language;
    setLanguage: (lang: Language) => void;
    t: (key: string) => string;
    dir: 'ltr' | 'rtl';
}

const LanguageContext = createContext<LanguageContextType | undefined>(undefined);

export const LanguageProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
    const [language, setLanguage] = useState<Language>('en');

    // Load language from local storage on mount
    useEffect(() => {
        try {
            const savedLang = localStorage.getItem('kca_language') as Language;
            if (savedLang && ['en', 'ar', 'ha'].includes(savedLang)) {
                setLanguage(savedLang);
            }
        } catch (e) {
            console.error("Error reading language from localStorage", e);
        }
    }, []);

    // Save language to local storage when changed
    useEffect(() => {
        try {
            localStorage.setItem('kca_language', language);
            document.documentElement.dir = language === 'ar' ? 'rtl' : 'ltr';
            document.documentElement.lang = language;
        } catch (e) {
            console.error("Error saving language to localStorage", e);
        }
    }, [language]);

    const t = (key: string): string => {
        if (!translations) {
            console.error("Translations object is undefined");
            return key;
        }

        const currentLang = language || 'en';
        const langData = translations[currentLang];

        if (langData && Object.prototype.hasOwnProperty.call(langData, key)) {
            return (langData as any)[key];
        }

        // Fallback to English
        if (currentLang !== 'en') {
            const enData = translations['en'];
            if (enData && Object.prototype.hasOwnProperty.call(enData, key)) {
                return (enData as any)[key];
            }
        }

        console.warn(`Missing translation for key: ${key} in language: ${currentLang}`);
        return key;
    };

    const dir = language === 'ar' ? 'rtl' : 'ltr';

    return (
        <LanguageContext.Provider value={{ language, setLanguage, t, dir }}>
            {children}
        </LanguageContext.Provider>
    );
};

export const useLanguage = () => {
    const context = useContext(LanguageContext);
    if (context === undefined) {
        throw new Error('useLanguage must be used within a LanguageProvider');
    }
    return context;
};
